/**
 * One place that decides what went wrong with an uploaded document, and what the
 * candidate should do about it.
 *
 * This exists because the decision used to live in two places that disagreed. The upload
 * page raised a toast carrying the real reason ("this account is in the name of ARJUN
 * MEHTA") and, underneath it, rendered a card built from a different branch of the same
 * data that said the photo was blurry. Bank documents had no branch at all, so every bank
 * upload — including the ones that passed — drew a red "Invalid Document" card. Three
 * near-identical copies of the JSX is what let that happen: the fourth case was simply
 * never added to any of them.
 *
 * So the rule here is that nothing renders a document problem without going through
 * buildDocumentStatus. It is deliberately pure — no React, no network, no formatting
 * beyond strings — so every case below can be pinned by a unit test.
 *
 * The distinction the whole module turns on: **what would the candidate have to change?**
 *   - a different photo of the same document  -> RETAKE
 *   - a different document                    -> REPLACE
 * Getting that wrong is not a wording problem. Telling someone their photo is unclear
 * when the real problem is that the account belongs to somebody else sends them back to
 * re-shoot a document that will never be accepted, for as long as they are willing.
 */

/** Stable identifiers for each way a document can fail. */
export const DOC_PROBLEM = {
  NAME_MISMATCH: 'name_mismatch',
  DOB_MISMATCH: 'dob_mismatch',
  NAME_UNVERIFIED: 'name_unverified',
  NOT_A_BANK_DOCUMENT: 'not_a_bank_document',
  WRONG_DOCUMENT_TYPE: 'wrong_document_type',
  UNREADABLE: 'unreadable',
  READ_FAILED: 'read_failed',
};

/** What the candidate has to do next. Drives the button, and nothing else. */
export const FIX = {
  /** A better photo of the same document will work. */
  RETAKE: 'retake',
  /** No photo of this document will work; they need a different one. */
  REPLACE: 'replace',
};

/**
 * Fields asked for again when nothing confirmed an Aadhaar — no name found and no DOB to
 * decide on. Gender is not among them: it is not identity evidence, and "IEMALE" read off a
 * card whose name had just matched once sent a candidate back for a retake she never made.
 */
export const REQUIRED_AADHAAR_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'dob', label: 'Date of Birth' },
];

/**
 * Read failures the candidate owns, as named by `extractTextLines`. Everything else the
 * server can report — a validator that is down, unreachable or timing out — is ours, and
 * the default branch apologises for it. Listing ours instead would mean a reason code
 * added later silently became the candidate's fault.
 */
const FILE_SIDE_READ_FAILURES = new Set([
  'PDF_PASSWORD_PROTECTED', 'PDF_UNOPENABLE', 'PDF_PAGE_UNREADABLE', 'PDF_PAGE_MISSING',
]);

/**
 * Nothing was read, so nothing may be said about the document itself.
 *
 * Shared by both paths because the upload page had grown its own copy of this card for
 * the network-failure case — the exact duplication this module exists to prevent — and
 * the two wordings then drifted apart.
 *
 * It does not say whose fault the failure was. "This is a problem on our side, not with
 * your document" reads as an excuse, gives the candidate nothing to act on, and made
 * people angry rather than reassured. What they need is the next step: type the number.
 */
function readFailedStatus({ errorReason, numberLabel }) {
  // A read that ran out of time is the one failure here that is nobody's fault and
  // that trying again genuinely fixes. It must not borrow the photo-quality wording:
  // a candidate told six times to take a sharper photo takes six sharper photos, and
  // every one of them times out in the same place.
  if (errorReason === 'VALIDATOR_TIMEOUT') {
    return {
      code: DOC_PROBLEM.READ_FAILED,
      title: 'This took too long to check',
      message:
        'Your file was saved, but the check did not finish in time. Try again — it usually goes through on the second attempt. Nothing is wrong with your photo.',
      fix: FIX.RETAKE,
      actionLabel: 'Try again',
      comparison: null,
      tips: [],
      helpNote: null,
      allowManualEntry: true,
    };
  }

  // A locked statement is the commonest bank PDF there is, and it has an exact fix:
  // the bank app's "download without password" option, or a screenshot of the page.
  if (errorReason === 'PDF_PASSWORD_PROTECTED') {
    return {
      code: DOC_PROBLEM.READ_FAILED,
      title: 'This PDF is locked with a password',
      message:
        'Upload an unlocked copy, or a screenshot of the page that shows your name, account number and IFSC code.',
      fix: FIX.REPLACE,
      actionLabel: 'Upload a different file',
      comparison: null,
      tips: [],
      helpNote: null,
      allowManualEntry: true,
    };
  }

  if (FILE_SIDE_READ_FAILURES.has(errorReason)) {
    return {
      code: DOC_PROBLEM.READ_FAILED,
      title: 'We could not open this PDF',
      message:
        'Bank PDFs are often password-protected. Send a photo or screenshot of your passbook, cheque or bank app instead.',
      fix: FIX.REPLACE,
      actionLabel: 'Upload a different file',
      comparison: null,
      tips: [],
      helpNote: null,
      allowManualEntry: true,
    };
  }

  return {
    code: DOC_PROBLEM.READ_FAILED,
    title: 'We could not read this document',
    message: `Type your ${numberLabel} below to continue. Your file has been saved.`,
    fix: FIX.RETAKE,
    actionLabel: 'Try uploading again',
    comparison: null,
    tips: [],
    helpNote: null,
    allowManualEntry: true,
  };
}

/** The bank step, which is verified by reading text rather than by classifying a card. */
export function isBankDoc(doc) {
  return doc?.type === 'bank_details' || doc?.subtype === 'bank_account';
}

/**
 * OCR failures on these fields rarely come back empty — they come back as punctuation
 * noise ("{", ";", "1"). Treat anything that isn't a plausible value as unread.
 */
export function isFieldLegible(key, value) {
  const text = String(value ?? '').trim();
  if (!text) return false;
  if (key === 'gender') return /^(male|female|transgender|other|m|f|t)$/i.test(text);
  if (key === 'dob') return /(19|20)\d{2}/.test(text);
  return /[a-zA-Z]{2}/.test(text);
}

export function checkDocumentTypeMatch(docSubtype, detectedDocType) {
  const normSubtype = String(docSubtype || '').toLowerCase();
  const normDetected = String(detectedDocType || '').toLowerCase();

  if (normSubtype.includes('aadhaar') || normSubtype.includes('aadhar')) {
    return normDetected.includes('aadhaar') || normDetected.includes('aadhar');
  }
  if (normSubtype.includes('pan')) {
    return normDetected.includes('pan');
  }
  if (normSubtype.includes('passport')) {
    return normDetected.includes('passport');
  }
  if (
    normSubtype.includes('driving') ||
    normSubtype.includes('licence') ||
    normSubtype.includes('license') ||
    normSubtype === 'dl'
  ) {
    return (
      normDetected.includes('driving') ||
      normDetected.includes('license') ||
      normDetected.includes('licence') ||
      normDetected.includes('dl')
    );
  }
  if (normSubtype.includes('voter')) {
    return normDetected.includes('voter');
  }
  return true;
}

/** Turn a classifier label ("Pan_Card") into something a candidate can read. */
export function friendlyDocTypeName(detectedType) {
  const normalized = String(detectedType || '').toLowerCase();
  if (normalized.includes('pan')) return 'a PAN Card';
  if (normalized.includes('aadhaar') || normalized.includes('aadhar')) return 'an Aadhaar Card';
  if (normalized.includes('passport')) return 'a Passport';
  if (normalized.includes('driving') || normalized.includes('dl')) return 'a Driving Licence';
  if (normalized.includes('voter')) return 'a Voter ID';
  return 'a different document';
}

export function formatFieldList(labels) {
  const list = (labels || []).filter(Boolean);
  if (list.length <= 1) return list[0] || '';
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

/** Photo advice, shown only where a photo is actually the thing to change. */
const PHOTO_TIPS = (what) => [
  `Put the ${what} on a flat surface in good light`,
  'Keep every edge inside the frame — do not crop',
  'Avoid shadows, glare and reflections',
  'Hold the phone steady, directly above',
];

/** A card photographed on its side needs turning, not better light. */
const SIDEWAYS_TIPS = (what) => [
  `Turn the ${what} so the printing runs left to right`,
  `Put the ${what} on a flat surface in good light`,
  'Keep every edge inside the frame — do not crop',
  'Hold the phone steady, directly above',
];

/**
 * The card was read, but not everything printed on it was.
 *
 * Which of the two answers this gives matters more than it looks. A card the service
 * had to turn a quarter-turn before it could read anything was photographed on its
 * side, and no amount of extra sharpness will fix that — one candidate was told six
 * times that a perfectly sharp PAN was not sharp enough, and every retake was sharp
 * and sideways again. `appliedRotationDeg` is the service saying which of the two
 * problems this actually is, so the card can ask for the thing that would work.
 */
function unreadableFields({ missing, label, appliedRotationDeg }) {
  const rotation = Number(appliedRotationDeg || 0);
  const sideways = rotation === 90 || rotation === 270;
  return {
    code: DOC_PROBLEM.UNREADABLE,
    title: `We could not read ${formatFieldList(missing) || 'your details'}`,
    message: sideways
      ? `Your ${label} came through on its side, so not everything printed on it could be read. Take the photo with the card the right way up, so the printing runs left to right.`
      : `Everything printed on your ${label} has to be sharp enough for us to check it against your application.`,
    fix: FIX.RETAKE,
    actionLabel: 'Retake the photo',
    comparison: null,
    tips: sideways ? SIDEWAYS_TIPS('card') : PHOTO_TIPS('card'),
    helpNote: null,
    allowManualEntry: false,
  };
}

/**
 * The line offered to someone blocked by a name that really is theirs.
 *
 * Name changes after marriage, initials expanded differently by a bank, a spelling the
 * employer's records use — all real, all indistinguishable here from the wrong person's
 * document. The check stays strict, because it is what keeps a salary from being paid
 * into a stranger's account. But a candidate who is genuinely stuck needs somewhere to
 * go that is not "try again forever", so the card always names one.
 */
const NAME_HELP =
  'If your name has changed or is spelt differently, your recruiter can correct it. This check cannot be skipped.';

/**
 * The side-by-side names under a mismatch.
 *
 * The applied name is the one the server actually compared (`comparedName`), not the
 * one in the page's URL. Those two used to differ after a re-apply, and a candidate was
 * shown their own name twice, identically, under "belongs to someone else".
 */
const nameComparison = (documentName, candidateName, nameMatch) => ({
  documentLabel: 'Name on document',
  documentValue: documentName,
  appliedLabel: 'Name you applied with',
  appliedValue: nameMatch?.comparedName || candidateName || 'your application',
});

/**
 * A close name refused only because the date of birth differs. Shown as its own card:
 * "belongs to someone else" over two near-identical names tells the candidate nothing.
 * A name that shares nothing stays a name mismatch whatever the date says.
 */
const dobMismatch = (nameMatch) =>
  nameMatch?.dob === 'contradict' && ['partial', 'unread'].includes(nameMatch?.nameLevel);

const dobProblem = (documentDob, label) => ({
  code: DOC_PROBLEM.DOB_MISMATCH,
  title: 'Date of birth does not match',
  message: documentDob
    ? `The date of birth on your ${label} (${documentDob}) does not match the one you entered when applying.`
    : `The date of birth on your ${label} does not match the one you entered when applying.`,
  fix: FIX.RETAKE,
  actionLabel: `Upload ${label} again`,
  comparison: null,
  tips: [],
  helpNote: 'If the date you entered is wrong, your recruiter can correct it. This check cannot be skipped.',
  allowManualEntry: false,
});

/**
 * How a name check reads once the document was accepted — for the candidate's success
 * panel and the recruiter's viewer. Null when there is nothing to say.
 *
 * `needsReview` means accepted without enough to confirm the name: the candidate
 * carries on, and a recruiter looks. It must not read as a failure to the candidate,
 * who did nothing wrong, nor as a verified match to the recruiter, who has to look.
 */
export function nameMatchSummary(nameMatch) {
  if (!nameMatch) return null;
  if (nameMatch.needsReview && nameMatch.dob === 'contradict') {
    return {
      tone: 'review',
      candidateText: 'The date of birth here does not match your application. Your recruiter will confirm it — you can carry on.',
      recruiterText: 'DOB Review Needed',
      reason: nameMatch.reason || null,
    };
  }
  if (nameMatch.needsReview && nameMatch.basis === 'dob') {
    return {
      tone: 'review',
      candidateText: 'We could not confirm your name on this card, but your date of birth matches. Your recruiter will confirm it — you can carry on.',
      recruiterText: 'Name Review Needed',
      reason: nameMatch.reason || null,
    };
  }
  if (nameMatch.needsReview) {
    return {
      tone: 'review',
      candidateText: 'Your name is spelt a little differently here. Your recruiter will confirm it — you can carry on.',
      recruiterText: 'Name Review Needed',
      reason: nameMatch.reason || null,
    };
  }
  if (nameMatch.match) {
    return {
      tone: 'match',
      candidateText: 'Name matches your application',
      recruiterText: 'Verified Match',
      reason: nameMatch.anchor ? `Matched via the accepted ${nameMatch.anchor.source}` : null,
    };
  }
  return {
    tone: 'mismatch',
    candidateText: null,
    recruiterText: 'Name Review Needed',
    reason: nameMatch.reason || null,
  };
}

/**
 * The problem with an uploaded document, or null when there is none.
 *
 * Returning null is meaningful: it is what tells the caller to fall through to the
 * success panels. A caller must never invent a failure card of its own.
 *
 * @param {object} args
 * @param {{label?: string, subtype?: string, type?: string}} args.doc - the requested document
 * @param {object|null} args.result - the /verify-id response for this file
 * @param {string} args.candidateName - the name on the application
 * @returns {null | {
 *   code: string, title: string, message: string, fix: string, actionLabel: string,
 *   comparison: object|null, tips: string[], helpNote: string|null, allowManualEntry: boolean
 * }}
 */
export function buildDocumentStatus({ doc, result, candidateName }) {
  if (!result) return null;
  const label = doc?.label || 'document';

  return isBankDoc(doc)
    ? bankStatus({ result, candidateName, label })
    : govtIdStatus({ doc, result, candidateName, label });
}

function bankStatus({ result, candidateName, label }) {
  const bankCheck = result?.bankCheck;

  // The pipeline itself failed, so nothing is known about the document — not even
  // whether it is a bank document. Every branch below this one reasons about text that
  // was never read, which is why this guard comes first.
  if (!result?.success || !bankCheck) {
    return readFailedStatus({ errorReason: result?.errorReason, numberLabel: 'account number' });
  }

  if (bankCheck.ok) return null;

  if (bankCheck.notBankDocument) {
    // Naming the document they actually sent saves a round trip. An Aadhaar card in
    // this slot used to be reported as an account belonging to somebody else, because
    // its UID was accepted as an account number and its "Government of India" header
    // as the holder's name — an accusation produced by the wrong file.
    const looksLikeId = !!bankCheck.govtIdPhrase;
    return {
      code: DOC_PROBLEM.NOT_A_BANK_DOCUMENT,
      title: 'This is not a bank document',
      message: looksLikeId
        ? 'Aadhaar and PAN have their own steps. Here, upload your passbook, cheque, bank statement, or a screenshot of your bank app.'
        : 'Upload your passbook, cheque, bank statement, or a screenshot of your bank app. It must show your account number and IFSC code.',
      fix: FIX.REPLACE,
      actionLabel: 'Choose a bank document',
      comparison: null,
      tips: [],
      helpNote: null,
      allowManualEntry: false,
    };
  }

  const holder = bankCheck.fields?.accountHolderName || '';

  if (bankCheck.nameMismatch) {
    return {
      code: DOC_PROBLEM.NAME_MISMATCH,
      // Not "this account belongs to someone else". That is an accusation, and the
      // candidate reads it as one — while the two names are already shown side by side
      // underneath, which states the same fact without the charge.
      title: 'This account is in a different name',
      message: 'Your salary will be paid into this account, so it must be in your own name.',
      fix: FIX.REPLACE,
      actionLabel: 'Upload my own bank document',
      comparison: nameComparison(holder, candidateName, result?.nameMatch),
      tips: [],
      helpNote: NAME_HELP,
      allowManualEntry: false,
    };
  }

  // A server that reports `canContinue` has already decided that nothing here is the
  // candidate's to fix but the fields it could not read: an unconfirmed name goes to the
  // recruiter instead of stopping them, and every field is in the form below to check or
  // type. Stopping them on our own uncertainty is what kept 28 of 37 candidates from
  // ever getting past this step.
  //
  // No card for fields it could not read, either: the form leaves those empty and asks for
  // them in place. A red card here ("Upload a clearer copy") pointed the other way, and
  // candidates re-uploaded the same passbook a dozen times instead of typing an IFSC.
  if (bankCheck.canContinue) return null;

  // A name came off the document but too little of it agrees to confirm or deny — a bank
  // printing "Rakshitha K" against an applicant "kavi priya" shares only the letter K.
  // Re-shooting prints the same name again, so this is REPLACE, not RETAKE. It is kept
  // apart from a flat mismatch because we are not accusing them of anything.
  if (bankCheck.nameUnverified) {
    return {
      code: DOC_PROBLEM.NAME_UNVERIFIED,
      title: 'We could not match the name on this document',
      message: 'Upload a bank document showing the same name you applied with.',
      fix: FIX.REPLACE,
      actionLabel: 'Upload my own bank document',
      comparison: nameComparison(holder, candidateName, result?.nameMatch),
      tips: [],
      helpNote: NAME_HELP,
      allowManualEntry: false,
    };
  }

  // Everything left is genuinely unread, and a clearer photo is the fix.
  const missing = [...(bankCheck.missing || [])];
  if (bankCheck.nameUnreadable && !missing.includes('Account Holder Name')) {
    missing.push('Account Holder Name');
  }

  return {
    code: DOC_PROBLEM.UNREADABLE,
    title: missing.length
      ? `We could not read ${formatFieldList(missing)}`
      : 'We could not read this document',
    message:
      'Your name, account number and IFSC code must all be visible in one photo. Take it again with the whole document in the frame.',
    fix: FIX.RETAKE,
    actionLabel: 'Retake the photo',
    comparison: null,
    tips: PHOTO_TIPS('document'),
    helpNote: null,
    allowManualEntry: true,
  };
}

function govtIdStatus({ doc, result, candidateName, label }) {
  // The read itself failed, so every judgement below — which card this is, whose name is
  // on it — would be made about text that was never produced.
  if (!result?.success) {
    return readFailedStatus({ errorReason: result?.errorReason, numberLabel: 'document number' });
  }

  const validation = result?.validation;
  const detectedType = String(validation?.docType || '').toLowerCase();
  const idNumber = validation?.normalizedData?.id_number;
  const typeMatches = checkDocumentTypeMatch(doc?.subtype, detectedType);

  // The classifier is confident this is a different card than the slot asked for.
  // Re-shooting will not change what card it is.
  const declaredMismatch = result?.docTypeMismatch;
  if (declaredMismatch) {
    return {
      code: DOC_PROBLEM.WRONG_DOCUMENT_TYPE,
      title: `This is not ${aOrAn(label)}`,
      message: `It looks like ${friendlyDocTypeName(declaredMismatch.detected)}, but this step needs your ${label}.`,
      fix: FIX.REPLACE,
      actionLabel: `Upload my ${label}`,
      comparison: null,
      tips: [],
      helpNote: null,
      allowManualEntry: false,
    };
  }

  const readCleanly = !!(result?.success && validation?.isValidFormat && idNumber && typeMatches);

  if (readCleanly) {
    if (doc?.subtype === 'aadhaar_card') {
      const aadhaar = aadhaarProblem({ result, validation, candidateName, label });
      if (aadhaar) return aadhaar;
    }
    if (doc?.subtype === 'pan_card') {
      const pan = panProblem({ result, candidateName, label });
      if (pan) return pan;
    }
    return null;
  }

  const recognised = !!validation?.isRecognizedId;
  if (!typeMatches && recognised && detectedType && detectedType !== 'unrecognized') {
    return {
      code: DOC_PROBLEM.WRONG_DOCUMENT_TYPE,
      title: `This is not ${aOrAn(label)}`,
      message: `It looks like ${friendlyDocTypeName(detectedType)}, but this step needs your ${label}.`,
      fix: FIX.REPLACE,
      actionLabel: `Upload my ${label}`,
      comparison: null,
      tips: [],
      helpNote: null,
      allowManualEntry: false,
    };
  }

  return {
    code: DOC_PROBLEM.UNREADABLE,
    title: `We could not read your ${label}`,
    message: `The number on the card has to be sharp enough to read. Take the photo again with the whole ${label} in frame.`,
    fix: FIX.RETAKE,
    actionLabel: 'Retake the photo',
    comparison: null,
    tips: PHOTO_TIPS('card'),
    helpNote: null,
    allowManualEntry: true,
  };
}

function aadhaarProblem({ result, validation, candidateName, label }) {
  const extractedName = validation?.normalizedData?.name || '';
  const nameMatch = result?.nameMatch;

  // The server's decision lets them continue: their name is on the card, or their date of
  // birth matches (then a recruiter checks). Nothing else on the card may send it back.
  if (nameMatch?.match === true) return null;

  const refused = nameMatch?.outcome === 'mismatch' || (extractedName && nameMatch && !nameMatch.outcome);
  if (refused && dobMismatch(nameMatch)) {
    return dobProblem(validation?.normalizedData?.dob, label);
  }

  if (refused && extractedName) {
    return {
      code: DOC_PROBLEM.NAME_MISMATCH,
      title: `This ${label} belongs to someone else`,
      message: `We can only accept your own ${label}.`,
      fix: FIX.REPLACE,
      actionLabel: `Upload my own ${label}`,
      comparison: nameComparison(extractedName, candidateName, result?.nameMatch),
      tips: [],
      helpNote: NAME_HELP,
      allowManualEntry: false,
    };
  }

  // Nothing confirmed the card — no name found and no DOB to decide on — so a clearer photo
  // is the one thing that can help.
  const missing = REQUIRED_AADHAAR_FIELDS.filter(
    (f) => !isFieldLegible(f.key, validation?.normalizedData?.[f.key])
  ).map((f) => f.label);

  if (missing.length) {
    return unreadableFields({
      missing,
      label,
      appliedRotationDeg: validation?.appliedRotationDeg,
    });
  }

  return null;
}

function panProblem({ result, candidateName, label }) {
  // The server's fieldCheck decides: accepted once the PAN number reads and the identity
  // decision lets the candidate continue (name found on the card, or a matching DOB).
  const check = result?.fieldCheck;
  if (!check || check.ok) return null;

  if (check.nameMismatch && (dobMismatch(result?.nameMatch) || !check.fields?.name)) {
    return dobProblem(check.fields?.dob, label);
  }

  if (check.nameMismatch) {
    return {
      code: DOC_PROBLEM.NAME_MISMATCH,
      title: `This ${label} belongs to someone else`,
      message: `We can only accept your own ${label}.`,
      fix: FIX.REPLACE,
      actionLabel: `Upload my own ${label}`,
      comparison: nameComparison(check.fields?.name || '', candidateName, result?.nameMatch),
      tips: [],
      helpNote: NAME_HELP,
      allowManualEntry: false,
    };
  }

  return unreadableFields({
    missing: check.missing,
    label,
    appliedRotationDeg: result?.validation?.appliedRotationDeg,
  });
}

function aOrAn(label) {
  return /^[aeiou]/i.test(String(label || '')) ? `an ${label}` : `a ${label}`;
}

/**
 * The warnings worth putting in front of a candidate.
 *
 * The server splits these by audience (see BANK_WARNINGS in bankDocumentParser). Older
 * responses carry only the combined `warnings` list, so fall back to it during a rolling
 * deploy rather than showing an empty panel or blowing up.
 */
export function candidateWarningsFrom(bankCheck, bank) {
  if (Array.isArray(bankCheck?.candidateWarnings)) return bankCheck.candidateWarnings;
  if (Array.isArray(bank?.candidateWarnings)) return bank.candidateWarnings;
  return bankCheck?.warnings || bank?.warnings || [];
}
